import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, ArrowRight, Clock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function BookingSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const isFree = searchParams.get("free") === "true";

  // For free bookings, read params from URL
  const serviceName = searchParams.get("service_name") || "";
  const date = searchParams.get("date") || "";
  const time = searchParams.get("time") || "";
  const name = searchParams.get("name") || "";

  const [status, setStatus] = useState(isFree ? "confirmed" : "loading");
  const [booking, setBooking] = useState(null);
  const attemptsRef = useRef(0);

  // Intentional: empty dep array starts the payment-status polling loop once on mount; adding sessionId/poll to deps would restart the timer on every render and cause duplicate polls.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!sessionId) return;
    let timer;

    const poll = async () => {
      try {
        const res = await fetch(`${API}/api/payments/status/${sessionId}`, { credentials: "include" });
        const data = await res.json();
        if (data.payment_status === "paid") {
          setStatus("confirmed");
          setBooking(data.booking);
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= 8) {
          setStatus("pending_email");
          return;
        }
        timer = setTimeout(poll, 2500);
      } catch {
        attemptsRef.current += 1;
        if (attemptsRef.current >= 8) setStatus("pending_email");
        else timer = setTimeout(poll, 2500);
      }
    };

    timer = setTimeout(poll, 1500);
    return () => clearTimeout(timer);
  }, [sessionId]);

  const displayService = booking?.service_name || serviceName;
  const displayDate = booking?.date || date;
  const displayTime = booking?.time_slot || time;
  const displayName = booking?.user_name || name;

  return (
    <div className="bg-ivory min-h-screen">
      {/* Header */}
      <section className="bg-navy py-20 px-6 text-center">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7 }}
          className="font-lora text-xs tracking-[0.3em] uppercase text-gold mb-3">
          De Nicola Consulting Co.
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
          className="font-lora text-4xl lg:text-5xl text-ivory font-normal">
          {status === "loading" ? "Confirming Your Booking" : "Session Confirmed"}
        </motion.h1>
        <div className="w-10 h-px bg-gold mx-auto mt-5"></div>
      </section>

      {/* Content */}
      <section className="py-20 px-6">
        <div className="max-w-lg mx-auto">

          {status === "loading" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-8">
              <div className="w-10 h-10 border-2 border-navy border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
              <p className="font-serif text-navy/60 text-sm">Confirming your payment and booking...</p>
            </motion.div>
          )}

          {(status === "confirmed" || status === "pending_email") && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              {/* Icon */}
              <div className="flex justify-center mb-8">
                <div className="w-16 h-16 rounded-full bg-[#F4F3EF] border border-gold/40 flex items-center justify-center">
                  <CheckCircle size={28} className="text-gold" />
                </div>
              </div>

              {status === "pending_email" ? (
                <div className="text-center mb-8">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <Clock size={14} className="text-gold" />
                    <p className="font-lora text-xs tracking-widest uppercase text-gold">Processing</p>
                  </div>
                  <p className="font-serif text-navy/70 text-sm leading-relaxed">
                    Your payment is being processed. You will receive a confirmation email with your session details shortly.
                  </p>
                </div>
              ) : (
                <div className="text-center mb-8">
                  <p className="font-serif text-navy/70 text-sm leading-relaxed">
                    {displayName ? `${displayName}, your` : "Your"} advisory session has been confirmed.{" "}
                    A confirmation email with your calendar invitation has been sent.
                  </p>
                </div>
              )}

              {(displayService || displayDate) && (
                <div className="bg-white border border-soft-gray border-t-[3px] border-t-gold p-8 mb-8">
                  <p className="font-lora text-xs tracking-[0.25em] uppercase text-navy/40 mb-5">Session Details</p>
                  {displayService && (
                    <div className="flex py-2.5 border-b border-soft-gray">
                      <span className="text-navy/40 text-xs font-serif w-20 shrink-0">Service</span>
                      <span className="text-navy text-sm font-serif font-medium">{displayService}</span>
                    </div>
                  )}
                  {displayDate && (
                    <div className="flex py-2.5 border-b border-soft-gray">
                      <span className="text-navy/40 text-xs font-serif w-20 shrink-0">Date</span>
                      <span className="text-navy text-sm font-serif">{displayDate}</span>
                    </div>
                  )}
                  {displayTime && (
                    <div className="flex py-2.5">
                      <span className="text-navy/40 text-xs font-serif w-20 shrink-0">Time</span>
                      <span className="text-navy text-sm font-serif">{displayTime} (Eastern Time)</span>
                    </div>
                  )}
                </div>
              )}

              <p className="font-serif italic text-navy/50 text-sm text-center leading-relaxed mb-8 px-4">
                "This session is designed to bring structure and clarity to your next move."
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/" className="flex-1 flex items-center justify-center gap-2 border border-navy text-navy py-3 font-lora text-xs tracking-[0.15em] uppercase hover:bg-navy hover:text-ivory transition-all">
                  Return Home
                </Link>
                <Link to="/services" className="flex-1 flex items-center justify-center gap-2 bg-navy text-ivory py-3 font-lora text-xs tracking-[0.15em] uppercase hover:bg-navy/90 transition-all">
                  View Services <ArrowRight size={13} />
                </Link>
              </div>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
}
